import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import type { Patient } from "~/api/patients";
import { getUnit, getUnitPatients } from "~/api/units";
import {
  EditPatientModal,
  Header,
  PageWrapper,
  PaginatedTable,
  PFAStatusBadge,
  RemovePatientConfirmationModal,
} from "~/components";
import { usePaginatedQuery, useTableSearchParams } from "~/hooks";
import { ROUTES } from "~/router";
import { DropdownMenu, Pulse } from "~/ui";
import { SearchInput } from "~/ui/SearchInput";
import { formatBackendDate } from "~/utils";

const PAGE_SIZE = 5;

export const MODAL_TYPE_OPTIONS = {
  REMOVE: "remove",
  EDIT: "edit",
} as const;

export type ModalType =
  (typeof MODAL_TYPE_OPTIONS)[keyof typeof MODAL_TYPE_OPTIONS];

export const UnitDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    currentPage,
    setCurrentPage,
    sorting,
    orderBy,
    sortDirection,
    setSorting,
    search,
    setSearchText,
  } = useTableSearchParams();
  const [selectedPatient, setSelectedPatient] = useState<Patient>();
  const [modalOpen, setModalOpen] = useState<ModalType>();

  const unitId = Number(id);

  const { data: unitData } = useQuery({
    queryFn: () => getUnit(unitId),
    queryKey: ["getUnit", { unitId }],
    enabled: !!unitId,
  });

  const {
    query: { data, isLoading },
    totalItems,
  } = usePaginatedQuery({
    queryFn: () =>
      getUnitPatients(unitId, {
        search,
        page: currentPage,
        per_page: PAGE_SIZE,
        order_by: orderBy,
        sort_direction: sortDirection,
      }),
    enabled: !!unitId,
    queryKey: [
      "getUnitPatients",
      {
        unitId,
        search,
        page: currentPage,
        pageSize: PAGE_SIZE,
        orderBy,
        sortDirection,
      },
    ],
  });

  if (!unitId) {
    return <Navigate to={ROUTES.units} />;
  }

  const patients = data?.data ?? [];

  const handleRemovePatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setModalOpen(MODAL_TYPE_OPTIONS.REMOVE);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setModalOpen(MODAL_TYPE_OPTIONS.EDIT);
  };

  const columns: ColumnDef<Patient>[] = [
    {
      header: t("patients.full_name"),
      accessorKey: "name",
      cell: ({ row }) => {
        const patient = row.original;
        return patient ? (
          <Link
            to={ROUTES.patientDetail.replace(":id", patient.id.toString())}
            className="text-primary-40 underline dark:text-primary-80"
          >
            {patient.name} {patient.surname}
          </Link>
        ) : (
          <span>—</span>
        );
      },
      accessorFn: (patient) => `${patient.name} ${patient.surname}`,
    },
    {
      id: "next_test_date",
      header: t("unit_detail.next_pfa_exam"),
      accessorFn: (patient) =>
        patient.next_test_date
          ? formatBackendDate(patient.next_test_date)
          : "—",
    },
    {
      id: "insole",
      header: t("insoles.insole"),
      accessorFn: (patient) => patient.active_insole?.serial_number ?? "—",
    },
    {
      id: "last_test_score",
      header: t("unit_detail.last_updated_score"),
      accessorFn: (patient) =>
        patient.last_physical_fitness_assessment_test?.status ?? "—",
      cell: ({ row }) => {
        const patient = row.original;
        const lastPFAStatus =
          patient.last_physical_fitness_assessment_test?.status;
        return lastPFAStatus ? (
          <PFAStatusBadge status={lastPFAStatus} />
        ) : (
          <span>—</span>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row: { original } }) => (
        <div className="flex justify-end">
          <DropdownMenu
            buttonAriaLabel={t("unit_detail.view_options_for_name", {
              name: `${original.name} ${original.surname}`,
            })}
            options={[
              {
                label: t("patients.edit_patient_details"),
                onClick: () => handleEditPatient(original),
              },
              {
                label: t("patients.view_patient_profile"),
                onClick: () =>
                  navigate(
                    ROUTES.patientDetail.replace(":id", original.id.toString()),
                  ),
              },
              {
                label: t("patients.remove_patient"),
                onClick: () => handleRemovePatient(original),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const total = data?.pagination?.total;

  return (
    <PageWrapper>
      {unitData?.data.name ? (
        <Header
          title={unitData.data.name}
          badgeText={
            total != undefined
              ? t("unit_detail.patient_count", {
                  count: total,
                })
              : undefined
          }
        />
      ) : (
        <Pulse containerClassName="flex h-8 w-52 items-center" />
      )}
      <div className="flex flex-row items-center justify-between">
        <h3 className="text-xl">{t("unit_detail.all_patients")}</h3>
        <SearchInput
          placeholder={t("unit_detail.search_by_name_placeholder")}
          onValueChange={setSearchText}
        />
      </div>
      <div className="mt-4">
        <PaginatedTable
          data={patients}
          columns={columns}
          isLoadingData={isLoading}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sorting={sorting}
          setSorting={setSorting}
          totalRows={totalItems}
        />
      </div>
      {selectedPatient && unitData && (
        <>
          <RemovePatientConfirmationModal
            isOpen={modalOpen === MODAL_TYPE_OPTIONS.REMOVE}
            patient={selectedPatient}
            unitId={unitId}
            onClose={() => setModalOpen(undefined)}
          />
          <EditPatientModal
            isOpen={modalOpen === MODAL_TYPE_OPTIONS.EDIT}
            patient={selectedPatient}
            unit={unitData.data}
            onClose={() => setModalOpen(undefined)}
          />
        </>
      )}
    </PageWrapper>
  );
};
